import {
  selectUser,
  ItemDate,
  AlertDialogProps,
} from '@centerpiece/components';
import {
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
  useRef,
} from 'react';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import {
  usePatchEventMutation,
  usePatchTaskMutation,
  usePatchCoordinationMutation,
  useGetItemQuery,
  useGetCommentsQuery,
  useGetItemStatusesQuery,
  usePatchItemMutation,
  useGetAttachmentsQuery,
  item as itemAPI,
  appointment,
  useGetRelationshipsQuery,
  useGetRolesQuery,
  useGetProjectContactsQuery,
} from 'store/api';
import {
  Comment,
  Coordination,
  Event,
  Item,
  Task,
  Response,
  ItemStatus,
  Message,
  Project,
  Appointment,
  Relationship,
  Role,
  ProjectContact,
} from 'store/api/models';
import moment from 'moment';
import { getItemDates } from 'utils/project.utils';
import { useTransformRelationships } from 'utils/contacts.utils';

export type ItemContextContent = {
  id: number;
  project_id?: number;
  item?: Item;
  appointments?: Appointment[];
  itemLoading?: boolean;
  template?: boolean;
  patchItem: Function;
  coordination?: Coordination;
  comments?: Comment[];
  commentsLoading?: boolean;
  project: Project;
  event?: Event;
  task?: Task;
  numMsgs?: number;
  numAvails?: number;
  numChats?: number;
  item_type?: 'task' | 'event' | 'coordination';
  messages?: { message: Message; responses: Response[] }[];
  availabilities?: { message: Message; responses: Response[] }[];
  title: string;
  setTitle: React.Dispatch<React.SetStateAction<string>>;
  status: ItemStatus;
  setStatus: React.Dispatch<React.SetStateAction<ItemStatus>>;
  activeTab: string;
  setActiveTab: React.Dispatch<React.SetStateAction<string>>;
  submit: Function;
  refetchItem: Function;
  allattachments: any[];
  afterSave: Function;
  start_date?: string;
  due_date?: string;
  patchTask: (values: any) => void;
  dates?: ItemDate[];
  alert?: (alert: AlertDialogProps) => void;
  relationships: Relationship[];
  project_contacts?: ProjectContact[];
  roles?: Role[];
};

export const ItemContext = createContext<ItemContextContent>({
  id: 0,
  project_id: 0,
  project: {} as Project,
  patchItem: () => {},
  title: '',
  setTitle: () => {},
  status: {} as ItemStatus,
  setStatus: () => {},
  activeTab: '',
  setActiveTab: () => {},
  refetchItem: () => {},
  submit: () => {},
  allattachments: [],
  afterSave: () => {},
  patchTask: (values: any) => {},
  relationships: [],
});

export const ItemProvider = ({
  id,
  children,
  subType,
  template = false,
  afterSave = () => {},
  alert,
}: {
  id: number;
  template?: boolean;
  subType?: string[];
  children: any;
  afterSave?: Function;
  alert?: (alert: AlertDialogProps) => void;
}) => {
  const dispatch = useDispatch();
  const [patchItem] = usePatchItemMutation();
  const [patchEvent] = usePatchEventMutation();
  const [patchTaskActual] = usePatchTaskMutation();
  const [patchCoordination] = usePatchCoordinationMutation();
  const cacheBust = useRef(Date.now()).current;
  const {
    data: item,
    isLoading: itemLoading,
    refetch: refetchItemActual,
  } = useGetItemQuery({ id: id, params: { nested: 'all' } }, { skip: !id });
  const event = item?.event_r;
  const coordination = item?.coordination_r;
  const task = item?.task_r;
  const item_type = task ? 'task' : event ? 'event' : 'coordination';
  const [messages, availabilities]: [
    { message: Message; responses: Response[] }[],
    { message: Message; responses: Response[] }[]
  ] = useMemo(() => {
    let msgs: { message: Message; responses: Response[] }[] = [];
    let avails: { message: Message; responses: Response[] }[] = [];
    item?.messages?.forEach((m: Message) => {
      if (m.type === 'Default') {
        msgs.push({
          message: m,
          responses:
            item?.responses.filter(r => r.r_message_r.id === m.id) || [],
        });
      } else if (m.type === 'Availability') {
        avails.push({
          message: m,
          responses:
            item?.responses.filter(r => r.r_message_r.id === m.id) || [],
        });
      }
    });
    return [msgs, avails];
  }, [item?.messages, item?.responses]);
  const { data: relationships_raw = [] } = useGetRelationshipsQuery(
    {
      params: { item: id, nested: 'all' },
    },
    { skip: !id }
  );
  const relationships = useTransformRelationships(relationships_raw);
  const {
    data: comments = [],
    isLoading: commentsLoading,
    refetch: refetchComments,
  } = useGetCommentsQuery(
    { params: { nested: 'all', item: id } },
    { skip: !id }
  );
  const { contact: userContact } = useSelector(selectUser);
  const numMsgs = messages.reduce((count, { responses, message }) => {
    const newMessages = message?.contacts?.filter(
      (contact: any) =>
        contact.status === 'New' &&
        contact.id === userContact &&
        !contact.author
    );
    const newResponses = responses.filter(
      (response: Response) =>
        response?.contacts?.filter(
          (contact: any) =>
            contact.status === 'New' && contact.id === userContact
        )?.length > 0
    );
    return count + newMessages.length + newResponses.length;
  }, 0);
  const numAvails = availabilities.reduce((count, { responses }) => {
    const newResponses = responses.filter(
      (response: Response) =>
        response?.contacts?.filter(
          (contact: any) =>
            contact.status === 'New' && contact.id === userContact
        )?.length > 0
    );
    return count + newResponses.length;
  }, 0);
  const numChats = comments?.filter(
    (comment: Comment) =>
      comment?.contacts?.filter(
        (contact: any) =>
          contact.status === 'New' && contact.contact === userContact
      )?.length > 0
  )?.length;

  const dates = getItemDates(item);

  const start_date =
    task?.start_date || event?.start_date || coordination?.start_date;
  const due_date = subType?.includes('coordination')
    ? messages?.[0]?.message?.due_date
    : task?.due_date ||
      event?.due_date ||
      coordination?.due_date ||
      event?.form?.daterange?.[0]?.end;
  const refetchItem = async () => {
    await refetchItemActual();
    await refetchAttachments();
    await refetchComments();
  };
  const patchTask = async (values: any) => {
    await patchTaskActual(values);
    dispatch(itemAPI.util.invalidateTags([{ type: 'Item', id: id }]));
    await refetchItem();
  };
  const [title, setTitle] = useState<string>(
    task?.title || event?.title || coordination?.title || ''
  );
  const [status, setStatus] = useState<ItemStatus>(
    task?.status_r ||
      event?.e_status_r ||
      coordination?.crd_status_r ||
      ({} as ItemStatus)
  );
  const { data: attachments = [], refetch: refetchAttachments } =
    useGetAttachmentsQuery(
      {
        params: {
          item: id,
          project: item?.project,
          nested: 'all',
        },
      },
      { skip: !item?.project }
    );
  const { data: project_contacts = [], isLoading: projectContactsLoading } =
    useGetProjectContactsQuery(
      { params: { nested: 'all', project: item?.project, type: 'Internal' } },
      { skip: !item?.project }
    );
  const { data: roles = [] } = useGetRolesQuery({});
  useEffect(() => {
    if (task?.title || event?.title || coordination?.title)
      setTitle(task?.title || event?.title || coordination?.title || '');
    if (task?.status_r || event?.e_status_r || coordination?.crd_status_r)
      setStatus(
        task?.status_r ||
          event?.e_status_r ||
          coordination?.crd_status_r ||
          ({} as ItemStatus)
      );
  }, [item]);
  const location = useLocation();
  const initialTab = location.hash.match(/at=(\w+)/)?.[1] || 'info';
  const [activeTab, setActiveTab] = useState<string>(initialTab);
  const [manually, setManually] = useState<boolean>(
    event?.form?.manually || false
  );

  useEffect(() => setActiveTab(initialTab), [initialTab]);

  const submit = async ({
    newTitle,
    newStatus,
  }: {
    newTitle?: string;
    newStatus?: number;
  }) => {
    if (task) {
      await patchTask({
        id: task?.id,
        title: newTitle || title,
        status: newStatus || status?.id,
      } as Task);
    } else if (coordination) {
      await patchCoordination({
        id: coordination?.id,
        title: newTitle || title,
        status: newStatus || status?.id,
      } as Coordination);
    } else {
      await patchEvent({
        id: event?.id,
        title: newTitle || title,
        status: newStatus || status?.id,
      } as Event);
    }
    refetchItem();
    afterSave();
  };

  return (
    <ItemContext.Provider
      value={{
        id,
        project: item?.project_r || ({} as Project),
        project_id: item?.project || 0,
        item,
        itemLoading,
        patchItem,
        coordination,
        comments,
        commentsLoading,
        event,
        task,
        refetchItem,
        numChats,
        numMsgs,
        numAvails,
        submit,
        messages,
        availabilities,
        appointments: item?.appointments,
        item_type,
        status,
        setStatus,
        title,
        setTitle,
        activeTab,
        setActiveTab,
        template,
        allattachments: attachments,
        afterSave: async () => {
          await refetchItem();
          await afterSave();
        },
        start_date,
        due_date,
        patchTask,
        dates,
        alert,
        relationships,
        roles,
        project_contacts,
      }}
    >
      {children}
    </ItemContext.Provider>
  );
};

export const useItemContext = () => useContext(ItemContext);
